import { CheckCircle2 } from 'lucide-react';
import { useState, type FormEvent } from 'react';

import { AuthCard } from '@/components/AuthCard';
import { Button } from '@/components/ui/button';
import { Field, FormError } from '@/components/ui/field';
import { changePassword } from '@/lib/auth-client';
import { changePasswordSchema } from '@shared/schemas/user';

/**
 * Change a password while signed in.
 *
 * Reached from the avatar menu once the shell exists (slice 2.4). It is a route
 * of its own rather than a dialog because it is also where someone lands after
 * being given a generated password by an admin — `npm run user:create` prints
 * one and tells them to change it.
 */
export function ChangePasswordPage() {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [error, setError] = useState<string>();
  const [done, setDone] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  async function onSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    setError(undefined);
    setFieldErrors({});

    const parsed = changePasswordSchema.safeParse({
      currentPassword,
      newPassword,
      confirmPassword,
    });
    if (!parsed.success) {
      setFieldErrors(
        Object.fromEntries(
          parsed.error.issues.map((issue) => [String(issue.path[0]), issue.message]),
        ),
      );
      return;
    }

    setSubmitting(true);
    const result = await changePassword({
      currentPassword,
      newPassword,
      // Everything signed in elsewhere is ended. If the password is being
      // changed because someone else knew it, leaving their session alive
      // defeats the point.
      revokeOtherSessions: true,
    });
    setSubmitting(false);

    if (result.error) {
      setError('That current password is not right.');
      return;
    }

    setCurrentPassword('');
    setNewPassword('');
    setConfirmPassword('');
    setDone(true);
  }

  return (
    <AuthCard title="Change your password" description="At least 12 characters.">
      <form onSubmit={(event) => void onSubmit(event)} className="space-y-5" noValidate>
        <FormError>{error}</FormError>

        {done ? (
          <p
            role="status"
            aria-live="polite"
            className="flex items-start gap-2 text-body text-status-completed-text"
          >
            <CheckCircle2 aria-hidden="true" className="mt-0.5 size-[18px] shrink-0" />
            Password changed. Anything else signed in as you has been signed out.
          </p>
        ) : null}

        <Field
          label="Current password"
          name="current-password"
          type="password"
          autoComplete="current-password"
          required
          error={fieldErrors.currentPassword}
          value={currentPassword}
          onChange={(event) => {
            setCurrentPassword(event.target.value);
            setDone(false);
          }}
        />

        <Field
          label="New password"
          name="new-password"
          type="password"
          autoComplete="new-password"
          required
          error={fieldErrors.newPassword}
          hint="Twelve characters or more."
          value={newPassword}
          onChange={(event) => {
            setNewPassword(event.target.value);
            setDone(false);
          }}
        />

        <Field
          label="Confirm new password"
          name="confirm-password"
          type="password"
          autoComplete="new-password"
          required
          error={fieldErrors.confirmPassword}
          value={confirmPassword}
          onChange={(event) => {
            setConfirmPassword(event.target.value);
            setDone(false);
          }}
        />

        <Button type="submit" full size="lg" disabled={submitting}>
          {submitting ? 'Saving…' : 'Change password'}
        </Button>
      </form>
    </AuthCard>
  );
}
